// ============================================================================
// INPUT DATA BUILDER
// Utility for building algorithm-specific input data structures
// ============================================================================

import type { AlgorithmMeta } from '../registry/algorithmRegistry';

/** Dense 10-node weighted graph for rich visualization (>25 steps) */
export const DENSE_DEMO_GRAPH = {
  vertices: [
    { id: 'A', label: 'A', value: 'A' },
    { id: 'B', label: 'B', value: 'B' },
    { id: 'C', label: 'C', value: 'C' },
    { id: 'D', label: 'D', value: 'D' },
    { id: 'E', label: 'E', value: 'E' },
    { id: 'F', label: 'F', value: 'F' },
    { id: 'G', label: 'G', value: 'G' },
    { id: 'H', label: 'H', value: 'H' },
    { id: 'I', label: 'I', value: 'I' },
    { id: 'J', label: 'J', value: 'J' },
  ],
  edges: [
    { from: 'A', to: 'B', weight: 4 },
    { from: 'A', to: 'C', weight: 2 },
    { from: 'B', to: 'C', weight: 1 },
    { from: 'B', to: 'D', weight: 5 },
    { from: 'C', to: 'D', weight: 8 },
    { from: 'C', to: 'E', weight: 10 },
    { from: 'D', to: 'E', weight: 2 },
    { from: 'D', to: 'F', weight: 6 },
    { from: 'E', to: 'F', weight: 3 },
    { from: 'E', to: 'G', weight: 7 },
    { from: 'F', to: 'G', weight: 1 },
    { from: 'F', to: 'H', weight: 4 },
    { from: 'G', to: 'H', weight: 3 },
    { from: 'G', to: 'I', weight: 9 },
    { from: 'H', to: 'I', weight: 2 },
    { from: 'H', to: 'J', weight: 5 },
    { from: 'I', to: 'J', weight: 3 },
  ],
  startNode: 'A',
};

/** 10-node binary tree for traversal algorithms — BFS goes wide, DFS goes deep */
const TRAVERSAL_DEMO_GRAPH = {
  vertices: [
    { id: 'A', label: 'A', value: 'A' },
    { id: 'B', label: 'B', value: 'B' },
    { id: 'C', label: 'C', value: 'C' },
    { id: 'D', label: 'D', value: 'D' },
    { id: 'E', label: 'E', value: 'E' },
    { id: 'F', label: 'F', value: 'F' },
    { id: 'G', label: 'G', value: 'G' },
    { id: 'H', label: 'H', value: 'H' },
    { id: 'I', label: 'I', value: 'I' },
    { id: 'J', label: 'J', value: 'J' },
  ],
  edges: [
    { from: 'A', to: 'B', weight: 1 },
    { from: 'A', to: 'C', weight: 1 },
    { from: 'B', to: 'D', weight: 1 },
    { from: 'B', to: 'E', weight: 1 },
    { from: 'C', to: 'F', weight: 1 },
    { from: 'C', to: 'G', weight: 1 },
    { from: 'D', to: 'H', weight: 1 },
    { from: 'D', to: 'I', weight: 1 },
    { from: 'E', to: 'J', weight: 1 },
  ],
  startNode: 'A',
};

/** 8-node graph with multiple weighted paths for shortest-path algorithms */
const SHORTEST_PATH_DEMO_GRAPH = {
  vertices: [
    { id: 'S', label: 'S', value: 'S' },
    { id: 'A', label: 'A', value: 'A' },
    { id: 'B', label: 'B', value: 'B' },
    { id: 'C', label: 'C', value: 'C' },
    { id: 'D', label: 'D', value: 'D' },
    { id: 'E', label: 'E', value: 'E' },
    { id: 'F', label: 'F', value: 'F' },
    { id: 'T', label: 'T', value: 'T' },
  ],
  edges: [
    { from: 'S', to: 'A', weight: 7 },
    { from: 'S', to: 'B', weight: 3 },
    { from: 'A', to: 'C', weight: 2 },
    { from: 'B', to: 'C', weight: 1 },
    { from: 'B', to: 'D', weight: 5 },
    { from: 'C', to: 'D', weight: 3 },
    { from: 'C', to: 'E', weight: 4 },
    { from: 'D', to: 'E', weight: 1 },
    { from: 'D', to: 'T', weight: 2 },
    { from: 'E', to: 'T', weight: 6 },
    { from: 'F', to: 'T', weight: 1 },
    { from: 'S', to: 'F', weight: 10 },
  ],
  startNode: 'S',
};

/** 8-node tree (acyclic) for tree-based graph algorithms: HLD, centroid-decomp, rerooting-dp */
const TREE_DEMO_GRAPH = {
  vertices: [
    { id: '1', label: '1', value: '1' },
    { id: '2', label: '2', value: '2' },
    { id: '3', label: '3', value: '3' },
    { id: '4', label: '4', value: '4' },
    { id: '5', label: '5', value: '5' },
    { id: '6', label: '6', value: '6' },
    { id: '7', label: '7', value: '7' },
    { id: '8', label: '8', value: '8' },
  ],
  edges: [
    { from: '1', to: '2', weight: 1 },
    { from: '1', to: '3', weight: 1 },
    { from: '2', to: '4', weight: 1 },
    { from: '2', to: '5', weight: 1 },
    { from: '3', to: '6', weight: 1 },
    { from: '3', to: '7', weight: 1 },
    { from: '4', to: '8', weight: 1 },
  ],
  startNode: '1',
};

// ── ID-specific input overrides ──────────────────────────────────────────────
// Key: algorithmId (lowercase). Value: input data object.
// To add a new algorithm with custom input, just add one entry here.
// If an ID is not found, falls back to category-based defaults below.

const ID_OVERRIDES: Record<string, unknown> = {
  // ── Data Structures ──
  'segment-tree': { type: 'array', data: [1, 3, 5, 7, 9, 11] },
  'persistent-segment-tree': { type: 'generic', data: { arr: [1, 3, 5, 7, 9, 2, 4, 6], updates: [[0, 3, 10], [5, 2, 8]], queries: [[0, 3, 0], [0, 7, 1], [3, 5, 0]] } },
  'binary-lift': { type: 'generic', data: { edges: [[0,1],[0,2],[1,3],[1,4],[2,5],[2,6]], queries: [[3,4],[5,6],[3,6],[4,5]] } },
  'trie': { type: 'generic', data: { words: ['cat', 'car', 'dog', 'door', 'card'], operation: 'search', target: 'car' } },
  'disjoint-set-union': { type: 'generic', data: { size: 8, operations: [{ type: 'union', params: [0, 1] }, { type: 'union', params: [2, 3] }, { type: 'union', params: [1, 3] }, { type: 'find', params: [0, 3] }] } },
  'rope': { type: 'generic', data: { text: 'HelloWorld', operation: 'insert', params: [5, 'Beautiful'] } },
  'binomial-heap': { type: 'generic', data: { keys: [5, 10, 3, 8, 1, 7, 12, 4, 9, 2] } },

  // ── Tree ──
  'bst': { type: 'generic', data: { arr: [50, 30, 70, 20, 40, 60, 80], search: 40 } },

  // ── DP ──
  'knapsack': { type: 'generic', data: { weights: [2, 3, 4, 5, 6], values: [3, 4, 5, 6, 7], capacity: 8 } },
  'coin-change': { type: 'generic', data: { coins: [1, 2, 5], amount: 8 } },
  'lcs': { type: 'generic', data: { text1: 'ABCBDAB', text2: 'BDCABA' } },
  'edit-distance': { type: 'generic', data: { word1: 'kitten', word2: 'sitting' } },
  'matrix-chain': { type: 'generic', data: { dimensions: [5, 10, 3, 12, 5, 50, 6] } },
  'partition-equal-subset-sum': { type: 'generic', data: { nums: [1, 5, 11, 5] } },
  'word-break': { type: 'generic', data: { s: 'leetcode', wordDict: ['leet', 'code'] } },
  'longest-palindromic-subsequence': { type: 'generic', data: { s: 'bbbab' } },
  'lis': { type: 'generic', data: { arr: [10, 9, 2, 5, 3, 7, 101, 18] } },
  'fibonacci': { type: 'generic', data: { n: 10 } },

  // ── Greedy ──
  'huffman-coding': { type: 'generic', data: { text: 'abracadabra' } },
  'fractional-knapsack': { type: 'generic', data: { items: [{ id: 'A', weight: 10, value: 60 }, { id: 'B', weight: 20, value: 100 }, { id: 'C', weight: 30, value: 120 }], capacity: 50 } },
  'job-sequencing': { type: 'generic', data: { jobs: [{ id: 'J1', deadline: 2, profit: 100 }, { id: 'J2', deadline: 1, profit: 19 }, { id: 'J3', deadline: 2, profit: 27 }, { id: 'J4', deadline: 1, profit: 25 }, { id: 'J5', deadline: 3, profit: 15 }] } },
  'activity-selection': { type: 'generic', data: { activities: [{ name: 'A1', start: 1, finish: 4 }, { name: 'A2', start: 3, finish: 5 }, { name: 'A3', start: 0, finish: 6 }, { name: 'A4', start: 5, finish: 7 }, { name: 'A5', start: 3, finish: 9 }, { name: 'A6', start: 5, finish: 9 }, { name: 'A7', start: 6, finish: 10 }, { name: 'A8', start: 8, finish: 11 }] } },

  // ── Math ──
  'sieve-of-eratosthenes': { type: 'generic', data: { n: 50 } },
  'fast-exponentiation': { type: 'generic', data: { base: 3, exponent: 13, mod: 17 } },
  'modular-arithmetic': { type: 'generic', data: { a: 7, b: 5, mod: 13, operation: 'multiply' } },
  'extended-gcd': { type: 'generic', data: { a: 240, b: 46 } },
  'chinese-remainder': { type: 'generic', data: { remainders: [2, 3, 2], moduli: [3, 5, 7] } },
  'miller-rabin': { type: 'generic', data: { n: 53 } },

  // ── String ──
  'kmp': { type: 'generic', data: { text: 'ABABDABACDABABCABAB', pattern: 'ABABCABAB' } },
  'rabin-karp': { type: 'generic', data: { text: 'ABABDABACDABABCABAB', pattern: 'ABABCABAB' } },
  'boyer-moore': { type: 'generic', data: { text: 'ABABDABACDABABCABAB', pattern: 'ABABCABAB' } },
  'z-algorithm': { type: 'generic', data: { text: 'abacabadabacabae' } },
  'manacher': { type: 'generic', data: { text: 'abacabadabacabae' } },
  'rolling-hash': { type: 'generic', data: { text: 'abacabadabacabae', pattern: 'abac' } },
  'aho-corasick': { type: 'generic', data: { text: 'ahishershe', patterns: ['he', 'she', 'his', 'hers'] } },
  'suffix-array': { type: 'generic', data: { text: 'banana' } },
  'kasai-algorithm': { type: 'generic', data: { text: 'banana' } },
  'suffix-tree': { type: 'generic', data: { text: 'banana' } },
  'suffix-automaton': { type: 'generic', data: { text: 'banana' } },
  'ukkonen-algorithm': { type: 'generic', data: { text: 'banana' } },

  // ── Geometry ──
  'convex-hull': { type: 'generic', data: { points: [{ x: 0, y: 0 }, { x: 1, y: 3 }, { x: 3, y: 1 }, { x: 3, y: 3 }, { x: 5, y: 0 }, { x: 2, y: 2 }] } },
  'closest-pair': { type: 'generic', data: { points: [{ x: 0, y: 0 }, { x: 1, y: 3 }, { x: 3, y: 1 }, { x: 3, y: 3 }, { x: 5, y: 0 }, { x: 2, y: 2 }] } },
  'point-in-polygon': { type: 'generic', data: { polygon: [{ x: 0, y: 0 }, { x: 4, y: 0 }, { x: 4, y: 4 }, { x: 0, y: 4 }], point: { x: 2, y: 2 } } },
  'line-intersection': { type: 'generic', data: { line1: { p1: { x: 0, y: 0 }, p2: { x: 4, y: 4 } }, line2: { p1: { x: 0, y: 4 }, p2: { x: 4, y: 0 } } } },
  'line-segment-intersection': { type: 'generic', data: { line1: { p1: { x: 0, y: 0 }, p2: { x: 4, y: 4 } }, line2: { p1: { x: 0, y: 4 }, p2: { x: 4, y: 0 } } } },

  // ── Backtracking ──
  'n-queens': { type: 'generic', data: { n: 4 } },
  'sudoku-solver': { type: 'generic', data: { board: [[5,3,0,0,7,0,0,0,0],[6,0,0,1,9,5,0,0,0],[0,9,8,0,0,0,0,6,0],[8,0,0,0,6,0,0,0,3],[4,0,0,8,0,3,0,0,1],[7,0,0,0,2,0,0,0,6],[0,6,0,0,0,0,2,8,0],[0,0,0,4,1,9,0,0,5],[0,0,0,0,8,0,0,7,9]] } },
  'knights-tour': { type: 'generic', data: { n: 5 } },
  'rat-in-maze': { type: 'generic', data: { maze: [[1,0,0,0],[1,1,0,1],[0,1,0,0],[1,1,1,1]] } },
  'hamiltonian-cycle': { type: 'generic', data: { graph: [[0,1,1,1,0],[1,0,1,0,1],[1,1,0,1,1],[1,0,1,0,1],[0,1,1,1,0]] } },

  // ── Range Tree ──
  'range-tree-ds': { type: 'generic', data: { points: [{ id: 1, x: 0, y: 0 }, { id: 2, x: 1, y: 3 }, { id: 3, x: 3, y: 1 }, { id: 4, x: 3, y: 3 }, { id: 5, x: 5, y: 0 }, { id: 6, x: 2, y: 2 }], queries: [{ x_min: 0, x_max: 3, y_min: 0, y_max: 3 }] } },

  // ── Tree-based graph algorithms (need acyclic input) ──
  'heavy-light-decomposition': { type: 'graph', data: { ...TREE_DEMO_GRAPH, directed: false, weighted: false } },
  'centroid-decomposition': { type: 'graph', data: { ...TREE_DEMO_GRAPH, directed: false, weighted: false } },
  'rerooting-dp': { type: 'graph', data: { ...TREE_DEMO_GRAPH, directed: false, weighted: false } },
  'link-cut-tree': { type: 'graph', data: { ...TREE_DEMO_GRAPH, directed: false, weighted: false, operations: [
    { op: 'link', u: '4', v: '5', id: 'op1' },
    { op: 'connected', u: '4', v: '7', id: 'op2' },
    { op: 'cut', u: '2', v: '4', id: 'op3' },
    { op: 'connected', u: '8', v: '5', id: 'op4' },
  ] } },

  // ── DSU Rollback (needs operations array) ──
  'dsu-rollback': { type: 'graph', data: { ...DENSE_DEMO_GRAPH, directed: false, weighted: false, operations: [
    { op: 'union', u: 'A', v: 'B' },
    { op: 'union', u: 'C', v: 'D' },
    { op: 'connected', u: 'A', v: 'C' },
    { op: 'union', u: 'B', v: 'D' },
    { op: 'connected', u: 'A', v: 'C' },
    { op: 'rollback' },
    { op: 'connected', u: 'A', v: 'C' },
  ] } },
};

// ── Category-based default inputs ─────────────────────────────────────────────
// Used when no ID-specific override exists.

function categoryDefault(category: string, idLower: string): unknown {
  switch (category) {
    case 'sorting':
    case 'hybrid':
      return { type: 'array', data: [64, 34, 25, 12, 22, 11, 90, 88, 45, 50] };

    case 'graph':
    case 'graphs': {
      const directed = idLower.includes('topological') || idLower.includes('scc') || idLower.includes('flow') || idLower.includes('dinic') || idLower.includes('ford-fulkerson') || idLower.includes('edmonds') || idLower.includes('dominator') || idLower.includes('boykov') || idLower.includes('min-cut') || idLower.includes('kahns') || idLower.includes('euler') || idLower.includes('johnson');
      const isShortestPath = idLower.includes('dijkstra') || idLower.includes('bellman-ford') || idLower.includes('astar') || idLower.includes('floyd-warshall') || idLower.includes('johnson') || idLower.includes('min-cost-flow');
      const isTraversal = idLower.includes('bfs') || idLower.includes('dfs') || idLower.includes('connected-components');
      const selectedGraph = isShortestPath ? SHORTEST_PATH_DEMO_GRAPH : isTraversal ? TRAVERSAL_DEMO_GRAPH : DENSE_DEMO_GRAPH;
      return { type: 'graph', data: { ...selectedGraph, directed, weighted: true } };
    }

    case 'data structures':
    case 'data-structures':
      return { type: 'generic', data: { keys: [10, 5, 15, 2, 7, 12, 20], values: [10, 5, 15, 2, 7, 12, 20], arr: [10, 5, 15, 2, 7, 12, 20] } };

    case 'tree':
    case 'trees':
      return {
        type: 'tree',
        data: {
          nodes: [
            { id: '1', value: 10, left: '2', right: '3', parent: null, height: 2 },
            { id: '2', value: 5, left: '4', right: '5', parent: '1', height: 1 },
            { id: '3', value: 15, left: '6', right: '7', parent: '1', height: 1 },
            { id: '4', value: 2, parent: '2', height: 0 },
            { id: '5', value: 7, parent: '2', height: 0 },
            { id: '6', value: 12, parent: '3', height: 0 },
            { id: '7', value: 20, parent: '3', height: 0 },
          ],
          edges: [
            { from: '1', to: '2', edgeType: 'left' },
            { from: '1', to: '3', edgeType: 'right' },
            { from: '2', to: '4', edgeType: 'left' },
            { from: '2', to: '5', edgeType: 'right' },
            { from: '3', to: '6', edgeType: 'left' },
            { from: '3', to: '7', edgeType: 'right' },
          ],
        },
      };

    case 'matrix':
    case 'dp':
    case 'dynamic programming':
    case 'dynamic-programming':
      return {
        type: 'matrix',
        data: [
          [0, 0, 0, 0, 0, 0],
          [0, 0, 0, 0, 0, 0],
          [0, 0, 0, 0, 0, 0],
          [0, 0, 0, 0, 0, 0],
          [0, 0, 0, 0, 0, 0],
        ],
      };

    case 'greedy':
      return { type: 'generic', data: {} };

    case 'math':
    case 'number theory':
    case 'number-theory':
      return { type: 'generic', data: { n: 20 } };

    case 'string':
    case 'strings':
    case 'automaton':
    case 'compression':
    case 'cryptography':
      return { type: 'string', data: { word1: 'algorithm', word2: 'altruistic' } };

    case 'geometry':
    case 'geometric':
      return { type: 'generic', data: { points: [{ x: 0, y: 0 }, { x: 1, y: 3 }, { x: 3, y: 1 }, { x: 3, y: 3 }, { x: 5, y: 0 }, { x: 2, y: 2 }] } };

    case 'backtracking':
      return { type: 'generic', data: { n: 4 } };

    case 'game':
    case 'games':
    case 'game theory':
    case 'game-theory':
      return { type: 'generic', data: { depth: 3, branching: 2 } };

    case 'sat':
      return { type: 'generic', data: { clauses: [[1, 2], [-1, 2], [-2, 1]], nVars: 2 } };

    case 'range tree':
      return { type: 'generic', data: {} };

    case 'search':
      return { type: 'generic', data: { arr: [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31, 33, 35, 37, 39, 41, 43, 45, 47, 49, 51, 53, 55, 57, 59, 61], target: 45 } };

    default:
      return { type: 'generic', data: {} };
  }
}

/**
 * Builds input data structure for a given algorithm.
 * Priority: ID-specific override → category-based default → empty generic.
 */
export function buildInputData(algorithm: AlgorithmMeta): unknown {
  const idLower = algorithm.id.toLowerCase();
  const categoryLower = algorithm.category.toLowerCase();

  // 1. Check ID-specific overrides first
  if (ID_OVERRIDES[idLower]) return ID_OVERRIDES[idLower];

  // 2. Fall back to category-based defaults
  return categoryDefault(categoryLower, idLower);
}
